import { defaultHandleBettingMarketValueClick, getLabelValue, isMobileUserAgent } from '@sportal365/smp-widget-utils';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { DESKTOP, MOBILE } from '../../../../constants/deviceTypes';
import { GenericSize } from '../../../../constants/generic';
import { CurrencyPosition, IOddsV2, ISubscriberInfoFn, OddsMarketTypes, Themes } from '../../../../types';
import { ConfigContext, SMPContainer, SMPText, styled } from '../../../index';
import { StakesFooter } from '../StakesFooter';

interface StakesDisplayCustomProps {
  handleClick: ISubscriberInfoFn;
  initialAmount?: number;
  odds: IOddsV2;
  size?: GenericSize;
  theme?: Themes | null;
  currencyPosition?: CurrencyPosition;
}

const getResponsiveInputWidth = (number: number) => {
  const numDigits = number.toString().length;
  const digitsArray = Array.from({ length: numDigits }, (_, i) => (i % 2 === 0 ? '1' : '0'));

  return digitsArray.length * 10 + 5;
};

export const StakesDisplayCustom: FC<StakesDisplayCustomProps> = ({
  initialAmount = 10,
  handleClick,
  odds,
  size,
  theme,
  currencyPosition = 'prefix',
}) => {
  const { dataLabels } = useContext(ConfigContext);
  const [amount, setAmount] = useState(initialAmount);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedUrl, setSelectedUrl] = useState<string>();
  const maxAmount = 1000000;

  const calculateWins = useCallback(
    (teamOdds: string) => {
      return Math.round(amount * +teamOdds);
    },
    [amount],
  );

  useEffect(() => {
    const selections = odds[0].markets['1x2']?.selections;

    if (!selections) {
      return;
    }

    const numericOdds = selections.map((selection) => Number(selection.odds ?? 0));

    setSelectedIndex(selections.findIndex((selection) => Number(selection.odds ?? 0) === Math.max(...numericOdds)));
  }, [odds]);

  useEffect(() => {
    const selections = odds[0].markets['1x2']?.selections;

    if (!selections) {
      return;
    }

    const selection = selections[selectedIndex];

    setSelectedUrl(
      (selection.urls?.find(({ appType }) => appType === (isMobileUserAgent() ? MOBILE : DESKTOP)) ?? selection.urls?.[0])?.url ??
        undefined,
    );
  }, [odds, selectedIndex]);

  if (odds.length === 0) {
    return null;
  }

  const oddsBookmaker = odds[0].bookmaker;
  const oddsMarket = odds[0].markets['1x2'];

  if (!oddsBookmaker || !oddsMarket || !oddsMarket.selections) {
    return null;
  }

  const currency = getLabelValue(dataLabels?.stakes_currency, '€');
  const wins = calculateWins(oddsMarket.selections[selectedIndex].odds!);
  const handleOddCoefficientClick = (link: string) =>
    defaultHandleBettingMarketValueClick(handleClick, oddsBookmaker, OddsMarketTypes['ONEXTWO'], link);
  const isPrefix = currencyPosition === 'prefix';

  return (
    <SMPContainer
      css={{
        margin: 'auto',
        pt: size !== GenericSize.SMALL ? '$1' : '$2',
        pb: '$3',
        '& *': {
          boxSizing: 'border-box',
        },
      }}
    >
      <SelectionsContainer
        css={{
          rowGap: size !== GenericSize.SMALL ? '$2' : '$3',
          mx: size !== GenericSize.SMALL ? 'auto' : undefined,
          maxWidth: size !== GenericSize.SMALL ? '361px' : undefined,
        }}
      >
        {oddsMarket.selections.map((selection, index) => {
          const selected = selectedIndex === index;

          return (
            <SelectionButton
              key={selection.name}
              disabled={selection.odds === null}
              selected={selected}
              onClick={() => setSelectedIndex(index)}
              css={{ gridArea: `selection-${index}`, borderColor: theme === 'light' ? 'transparent' : undefined }}
            >
              <SMPText size='4' variant={selected ? undefined : 'contrast'}>
                {selection.name}
              </SMPText>
              <SMPText size='5' variant={selected ? undefined : 'contrast'} weight='700'>
                {selection.odds ?? '-'}
              </SMPText>
            </SelectionButton>
          );
        })}
        <AmountInputContainer>
          <SMPText size='2_5' variant='contrast' style={{ alignSelf: 'start' }}>
            {getLabelValue(dataLabels?.stakes_stake, 'Stake')}
          </SMPText>
          <div
            style={{
              flexDirection: isPrefix ? 'row-reverse' : 'row',
            }}
          >
            <input
              type='number'
              max={maxAmount}
              title={amount.toString()}
              value={amount.toString()}
              onChange={(e) => setAmount(Math.min(Math.max(1, +e.target.value), maxAmount))}
              style={{ width: getResponsiveInputWidth(amount), maxWidth: size !== GenericSize.SMALL ? 'unset' : '60px' }}
            />
            <SMPText size='5' weight='700' variant='contrast'>
              {currency}
            </SMPText>
          </div>
        </AmountInputContainer>
        <SelectionButton
          selected
          css={{
            gridArea: 'payout',
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            gap: '$0',
            alignItems: 'center',
            cursor: 'default',
          }}
        >
          <SMPText size='2_5' style={{ alignSelf: 'start' }}>
            {getLabelValue(dataLabels?.stakes_payout, 'Payout')}
          </SMPText>
          <div
            style={{
              display: 'flex',
              gap: '2px',
              justifyContent: 'end',
              flexDirection: isPrefix ? 'row-reverse' : 'row',
              alignItems: 'center',
            }}
          >
            <SMPText
              size='5'
              textAlign='right'
              title={wins.toString()}
              weight='700'
              style={{ maxWidth: size !== GenericSize.SMALL ? 'unset' : '60px', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {wins}
            </SMPText>
            <SMPText
              size='5'
              weight='700'
              style={{ maxWidth: size !== GenericSize.SMALL ? 'unset' : '60px', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {currency}
            </SMPText>
          </div>
        </SelectionButton>
        <CallToActionButton
          href={selectedUrl}
          target='_blank'
          onClick={() => selectedUrl && handleOddCoefficientClick(selectedUrl)}
          css={{ mt: size !== GenericSize.SMALL ? '$3' : '$2' }}
        >
          <SMPText size='5' textAlign='center' weight='700' style={{ lineHeight: '140%' }}>
            {getLabelValue(dataLabels?.stakes_bet, 'Bet now')}
          </SMPText>
        </CallToActionButton>
      </SelectionsContainer>
      <StakesFooter
        amount={amount}
        currency={currency}
        handleClick={handleClick}
        oddsBookmaker={oddsBookmaker}
        theme={theme}
        currencyPosition={currencyPosition}
      ></StakesFooter>
    </SMPContainer>
  );
};

const SelectionsContainer = styled.withConfig({ componentId: 'StakesDisplayCustom', displayName: 'SelectionsContainer' })('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gridTemplateAreas: `
    'selection-0 selection-0 selection-1 selection-1 selection-2 selection-2'
    'input input input payout payout payout'
    'call-to-action call-to-action call-to-action call-to-action call-to-action call-to-action'
  `,
  columnGap: '$3',
  mb: '$2',
});

export const SelectionButton = styled.withConfig({ componentId: 'StakesDisplayCustom', displayName: 'SelectionButton' })('button', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: '1px solid $gray7',
  borderRadius: '$1',
  py: '$2',
  px: '$2_5',
  backgroundColor: '$stakesSelectionBgColor',
  cursor: 'pointer',

  variants: {
    disabled: {
      true: {
        pointerEvents: 'none',
        opacity: 0.7,
      },
    },
    selected: {
      true: {
        borderColor: 'transparent',
        backgroundColor: '$stakesSelectedBgColor',
        color: '$selectedStakeColor',
      },
    },
  },
});

const AmountInputContainer = styled.withConfig({ componentId: 'StakesDisplayCustom', displayName: 'AmountInputContainer' })('div', {
  gridArea: 'input',
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  justifyContent: 'space-between',
  gap: '$0',
  alignItems: 'center',
  border: '1px solid $stakeInputBorderColor',
  borderRadius: '$1',
  py: '$2',
  px: '$2_5',
  backgroundColor: '$stakesSelectionBgColor',

  '& > div': {
    display: 'flex',
    gap: '2px',
    justifyContent: 'end',
    alignItems: 'center',
  },

  input: {
    border: 'none',
    p: '0',
    textAlign: 'end',
    backgroundColor: 'transparent',
    fontSize: '$5',
    fontWeight: '700',
    outline: 'none',
    color: '$hiContrast',
    '-moz-appearance': 'textfield',

    '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
});

const CallToActionButton = styled.withConfig({ componentId: 'StakesDisplayCustom', displayName: 'CallToActionButton' })('a', {
  gridArea: 'call-to-action',
  borderRadius: '$1',
  py: '$2',
  px: '$2_5',
  backgroundColor: '$accentPrimaryBgColor',
  color: '$accentContrastColor',
  textDecoration: 'none',
  cursor: 'pointer',
});

export default StakesDisplayCustom;
